import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Search, X, Fuel, Gauge, Cog, MapPin, MessageCircle, Info } from "lucide-react";
import { company } from "../mock";
import EngineQuoteForm from "./EngineQuoteForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;
const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300'><rect width='400' height='300' fill='#eef2f6'/><text x='50%' y='50%' font-family='Arial' font-size='20' fill='#94a3b8' text-anchor='middle' dominant-baseline='middle'>DEA S.R.L.</text></svg>"
  );

const fmtPrice = (n) => (n == null ? "-" : "€ " + n.toLocaleString("it-IT"));
const fmtKm = (n) => (n == null ? null : n.toLocaleString("it-IT") + " km");

function EngineCard({ e, onOpen, onQuote }) {
  const off =
    e.prezzo_originale && e.prezzo_vendita && e.prezzo_originale > e.prezzo_vendita
      ? Math.round((1 - e.prezzo_vendita / e.prezzo_originale) * 100)
      : null;
  return (
    <div className="engine-card">
      <div className="engine-img" onClick={() => onOpen(e)} style={{ cursor: "pointer" }}>
        <img src={e.foto_url || FALLBACK_IMG} alt={e.titolo} loading="lazy"
          onError={(ev) => { ev.target.src = FALLBACK_IMG; }} />
        <span className={`engine-tag ${e.stato === "Performance" ? "perf" : ""}`}>{e.stato}</span>
        <span className="engine-brand">{e.marca}</span>
      </div>
      <div className="engine-body">
        <h3 className="engine-title">{e.titolo}</h3>
        {e.codice_motore && <p className="engine-code">Cod. {e.codice_motore}</p>}
        <div className="engine-specs">
          {e.carburante && <span className="engine-spec">{e.carburante}</span>}
          {e.potenza_cv && <span className="engine-spec">{e.potenza_cv} CV</span>}
          {e.cilindri && <span className="engine-spec">{e.cilindri} cil.</span>}
          {e.chilometraggio != null && <span className="engine-spec">{fmtKm(e.chilometraggio)}</span>}
        </div>
        <div className="engine-price">
          <span className="sale">{fmtPrice(e.prezzo_vendita)}</span>
          {off && <span className="orig">{fmtPrice(e.prezzo_originale)}</span>}
          {off && <span className="off">-{off}%</span>}
        </div>
        <div className="engine-actions">
          <button className="engine-btn engine-btn-primary" onClick={() => onOpen(e)}>
            <Info size={15} /> Dettagli
          </button>
          <button className="engine-btn engine-btn-wa" onClick={() => onQuote(e)}>
            <MessageCircle size={15} /> Preventivo
          </button>
        </div>
      </div>
    </div>
  );
}

function EngineModal({ e, onClose, onQuote }) {
  if (!e) return null;
  const compat = (e.compatibilita || "").split(",").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="engine-modal-overlay" onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}>
      <div className="engine-modal">
        <button className="engine-modal-close" onClick={onClose} aria-label="Chiudi"><X size={18} /></button>
        <img className="engine-modal-img" src={e.foto_url || FALLBACK_IMG} alt={e.titolo}
          onError={(ev) => { ev.target.src = FALLBACK_IMG; }} />
        <div className="engine-modal-body">
          <span className="engine-spec" style={{ marginBottom: 8, display: "inline-block" }}>{e.marca} · {e.tipologia}</span>
          <h3>{e.titolo}</h3>
          {e.codice_motore && <p className="engine-code">Codice motore: {e.codice_motore}</p>}
          <div className="engine-price" style={{ margin: "1rem 0" }}>
            <span className="sale" style={{ fontSize: "1.8rem" }}>{fmtPrice(e.prezzo_vendita)}</span>
            {e.prezzo_originale && e.prezzo_originale > e.prezzo_vendita && (
              <span className="orig">{fmtPrice(e.prezzo_originale)}</span>
            )}
          </div>
          <div className="engine-modal-specs">
            <div className="spec-box"><div className="k">Carburante</div><div className="v">{e.carburante || "-"}</div></div>
            <div className="spec-box"><div className="k">Potenza</div><div className="v">{e.potenza_cv ? e.potenza_cv + " CV" : "-"}</div></div>
            <div className="spec-box"><div className="k">Cilindri</div><div className="v">{e.cilindri || "-"}</div></div>
            <div className="spec-box"><div className="k">Km</div><div className="v">{fmtKm(e.chilometraggio) || "-"}</div></div>
            <div className="spec-box"><div className="k">Tipologia</div><div className="v" style={{ fontSize: "0.85rem" }}>{e.tipologia}</div></div>
            <div className="spec-box"><div className="k">Stato</div><div className="v" style={{ fontSize: "0.85rem" }}>{e.stato}</div></div>
          </div>
          {compat.length > 0 && (
            <div className="compat-box">
              <h4>Compatibilità veicoli</h4>
              {compat.join(" · ")}
            </div>
          )}
          <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} onClick={() => onQuote(e)}>
            Richiedi preventivo
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Catalog() {
  const [filters, setFilters] = useState({ marche: [], carburanti: [], tipologie: [], total: 0 });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [quoteEngine, setQuoteEngine] = useState(null);

  const [search, setSearch] = useState("");
  const [marca, setMarca] = useState("");
  const [carburante, setCarburante] = useState("");
  const [tipologia, setTipologia] = useState("");
  const [sort, setSort] = useState("relevance");
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/filters`).then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  const fetchEngines = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12, sort };
      if (search) params.search = search;
      if (marca) params.marca = marca;
      if (carburante) params.carburante = carburante;
      if (tipologia) params.tipologia = tipologia;
      const r = await axios.get(`${API}/engines`, { params });
      setItems(r.data.items);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch (err) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, sort, search, marca, carburante, tipologia]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchEngines, search ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [fetchEngines, search]);

  // SEO: inject JSON-LD ItemList of currently loaded engines
  useEffect(() => {
    const id = "catalog-jsonld";
    let el = document.getElementById(id);
    if (!items.length) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Catalogo motori DEA S.R.L.",
      "numberOfItems": total,
      "itemListElement": items.map((e, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Product",
          "name": e.titolo,
          "sku": e.codice_motore || undefined,
          "brand": { "@type": "Brand", "name": e.marca },
          "image": e.foto_url || undefined,
          "category": e.tipologia,
          "offers": {
            "@type": "Offer",
            "price": e.prezzo_vendita,
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/UsedCondition"
          }
        }
      }))
    };
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
    el.textContent = JSON.stringify(data);
    return () => { const n = document.getElementById(id); if (n) n.remove(); };
  }, [items, total]);

  const resetPageAnd = (setter) => (v) => { setter(v); setPage(1); };
  const clearAll = () => { setSearch(""); setMarca(""); setCarburante(""); setTipologia(""); setSort("relevance"); setPage(1); };
  const hasFilters = search || marca || carburante || tipologia || sort !== "relevance";

  const goPage = (p) => {
    setPage(p);
    if (sectionRef.current) sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pageNums = () => {
    const arr = [];
    const around = 2;
    for (let i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || (i >= page - around && i <= page + around)) arr.push(i);
      else if (arr[arr.length - 1] !== "...") arr.push("...");
    }
    return arr;
  };

  const openQuote = (engine) => { setSelected(null); setQuoteEngine(engine); };

  return (
    <section id="catalogo" ref={sectionRef}>
      <div className="container">
        <div className="section-title reveal">
          <h2>Catalogo Motori Disponibili</h2>
          <p>Oltre {filters.total || total} motori testati e verificati, pronti alla spedizione. Cerca per marca, codice o modello e richiedi subito il preventivo.</p>
        </div>

        <div className="catalog-toolbar">
          <div className="catalog-search">
            <Search size={18} />
            <input type="text" placeholder="Cerca per marca, codice motore o modello…"
              value={search} onChange={(e) => resetPageAnd(setSearch)(e.target.value)} />
          </div>
          <div className="catalog-filters">
            <select className="catalog-select" value={marca} onChange={(e) => resetPageAnd(setMarca)(e.target.value)}>
              <option value="">Tutte le marche</option>
              {filters.marche.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select className="catalog-select" value={carburante} onChange={(e) => resetPageAnd(setCarburante)(e.target.value)}>
              <option value="">Carburante</option>
              {filters.carburanti.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="catalog-select" value={tipologia} onChange={(e) => resetPageAnd(setTipologia)(e.target.value)}>
              <option value="">Tipologia</option>
              {filters.tipologie.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="catalog-select" value={sort} onChange={(e) => resetPageAnd(setSort)(e.target.value)}>
              <option value="relevance">Ordina per</option>
              <option value="price_asc">Prezzo crescente</option>
              <option value="price_desc">Prezzo decrescente</option>
              <option value="power_desc">Potenza (CV)</option>
            </select>
          </div>
        </div>

        <div className="catalog-meta">
          <span>{loading ? "Caricamento…" : `${total.toLocaleString("it-IT")} motori trovati`}</span>
          {hasFilters && <button className="catalog-clear" onClick={clearAll}>✕ Rimuovi filtri</button>}
        </div>

        {loading ? (
          <div className="catalog-grid">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="catalog-skeleton" key={i}>
                <div className="sk-img" />
                <div className="sk-body">
                  <div className="sk-line" style={{ width: "90%" }} />
                  <div className="sk-line" style={{ width: "60%" }} />
                  <div className="sk-line" style={{ width: "40%" }} />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="catalog-empty">Nessun motore trovato con questi criteri. <button className="catalog-clear" onClick={clearAll}>Azzera i filtri</button></div>
        ) : (
          <div className="catalog-grid">
            {items.map((e) => <EngineCard key={e.id} e={e} onOpen={setSelected} onQuote={openQuote} />)}
          </div>
        )}

        {!loading && pages > 1 && (
          <div className="catalog-pagination">
            <button className="page-btn" disabled={page === 1} onClick={() => goPage(page - 1)}>←</button>
            {pageNums().map((p, i) =>
              p === "..." ? <span key={`d${i}`} style={{ color: "#94a3b8" }}>…</span>
                : <button key={p} className={`page-btn ${p === page ? "active" : ""}`} onClick={() => goPage(p)}>{p}</button>
            )}
            <button className="page-btn" disabled={page === pages} onClick={() => goPage(page + 1)}>→</button>
          </div>
        )}
      </div>

      <EngineModal e={selected} onClose={() => setSelected(null)} onQuote={openQuote} />
      <EngineQuoteForm engine={quoteEngine} onClose={() => setQuoteEngine(null)} />
    </section>
  );
}
